import React from 'react';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GOVERNANCE_COPY, rolePerfilLabel } from '@/lib/governanceCopy';

export interface SoDNoticeProps {
  /**
   * Either a business-friendly label (e.g. "Qualidade") or an internal role
   * identifier (e.g. "gestor_qualidade") that will be translated.
   */
  perfilEsperado: string;
  className?: string;
}

/**
 * SoDNotice
 *
 * Discreet, business-language explanation shown where an action button is
 * intentionally absent due to role separation. Educates the user (and
 * investors observing demos) that the restriction is a feature, not a gap.
 *
 * Renders no internal role names, no policy names, no error codes.
 */
export const SoDNotice: React.FC<SoDNoticeProps> = ({ perfilEsperado, className }) => {
  // Heuristic: if value matches a known internal role key, translate it.
  const looksInternal = /[_a-z]+_[a-z]+/.test(perfilEsperado);
  const label = looksInternal ? rolePerfilLabel(perfilEsperado) : perfilEsperado;

  return (
    <div
      role="note"
      aria-label={GOVERNANCE_COPY.sod.title}
      className={cn(
        'flex items-start gap-2 rounded-md border border-border/60 bg-muted/30',
        'px-3 py-2 text-[12px] text-muted-foreground',
        className,
      )}
    >
      <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 opacity-70" strokeWidth={1.7} />
      <div className="leading-snug">
        <span className="font-medium text-foreground/80">
          {GOVERNANCE_COPY.sod.title}
        </span>{' '}
        <span>{GOVERNANCE_COPY.sod.descriptionFor(label)}</span>
      </div>
    </div>
  );
};

export default SoDNotice;
